import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "../i18n/useI18n";
import { numberLocaleFor } from "../i18n/locale";
import { estimateFromPersistedSnapshot } from "../lib/estimateFromPersistedSnapshot";
import type { PersistedUi } from "../lib/persistedUi";
import {
  decodeScenarioHashFragment,
  normalizeImportedPersisted,
} from "../lib/scenarioBundle";

export function ScenarioPreviewPage() {
  const { t, locale } = useI18n();
  const location = useLocation();
  const nf = numberLocaleFor(locale);

  const { persisted, hashBody } = useMemo(() => {
    const rawHash = location.hash.startsWith("#")
      ? location.hash
      : location.hash
        ? `#${location.hash}`
        : "";
    if (!rawHash.startsWith("#dm=1.")) {
      return { persisted: null as PersistedUi | null, hashBody: "" };
    }
    const decoded = decodeScenarioHashFragment(rawHash);
    const p = decoded ? normalizeImportedPersisted(decoded, "") : null;
    const body = rawHash.startsWith("#") ? rawHash.slice(1) : rawHash;
    return { persisted: p, hashBody: body };
  }, [location.hash]);

  const estimate = useMemo(
    () => (persisted ? estimateFromPersistedSnapshot(persisted, locale) : null),
    [persisted, locale],
  );

  const fmt = (n: number) =>
    n.toLocaleString(nf, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

  if (!persisted || !estimate) {
    return (
      <main className="app preview-page">
        <h1 className="preview-h1">{t("preview.title")}</h1>
        <p className="preview-muted">{t("preview.invalid")}</p>
        <Link className="preview-link" to="/">
          {t("nav.home")}
        </Link>
        <style>{`
          .preview-page {
            max-width: 40rem;
            margin: 0 auto;
            padding: 2rem clamp(0.85rem, 4vw, 1.35rem) 4rem;
          }
          .preview-h1 {
            font-size: 1.25rem;
            margin: 0 0 0.75rem;
          }
          .preview-muted {
            color: var(--muted);
            line-height: 1.5;
            margin: 0 0 1rem;
          }
          .preview-link {
            color: var(--accent);
            font-weight: 600;
          }
        `}</style>
      </main>
    );
  }

  const notes = persisted.scenarioNotes?.trim();

  return (
    <main className="app preview-page">
      <h1 className="preview-h1">{t("preview.title")}</h1>
      <p className="preview-muted">{t("preview.subtitle")}</p>
      <p className="preview-total" aria-live="polite">
        {fmt(estimate.totalMonthlyUsd)}
        <span className="preview-per">{t("breakdown.perMonth")}</span>
      </p>
      {notes ? (
        <section className="preview-notes">
          <h2 className="preview-notes-h">{t("export.appendix.notesTitle")}</h2>
          <pre className="preview-notes-pre">{notes}</pre>
        </section>
      ) : null}
      <Link
        className="preview-link preview-cta"
        to={{ pathname: "/", search: location.search, hash: hashBody }}
      >
        {t("preview.openFull")}
      </Link>
      <style>{`
        .preview-page {
          max-width: 40rem;
          margin: 0 auto;
          padding: 2rem clamp(0.85rem, 4vw, 1.35rem) 4rem;
        }
        .preview-h1 {
          font-size: 1.25rem;
          margin: 0 0 0.75rem;
        }
        .preview-muted {
          color: var(--muted);
          line-height: 1.5;
          margin: 0 0 1rem;
        }
        .preview-total {
          font-size: clamp(1.5rem, 4vw, 2rem);
          font-weight: 700;
          color: var(--accent);
          margin: 0 0 1.25rem;
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 0.5rem;
        }
        .preview-per {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--muted);
        }
        .preview-notes {
          margin: 0 0 1.25rem;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: var(--surface2);
        }
        .preview-notes-h {
          font-size: 0.85rem;
          margin: 0 0 0.5rem;
          color: var(--muted);
        }
        .preview-notes-pre {
          margin: 0;
          white-space: pre-wrap;
          word-break: break-word;
          font-family: var(--font);
          font-size: 0.85rem;
          line-height: 1.45;
        }
        .preview-link {
          color: var(--accent);
          font-weight: 600;
        }
        .preview-cta {
          display: inline-flex;
        }
      `}</style>
    </main>
  );
}
